
import { Vue, Options, prop } from "vue-class-component";
import { Commodity, CommodityList } from "@/domain/entities/Commodity";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { CommodityController } from "@/app/ui/controllers/CommodityController";
import { debounce } from "lodash";
import { RequestListCommodity } from "@/data/payload/api/CommodityApiRequest";
import { ellipsisString } from "@/app/infrastructures/misc/Utils";
import OverlayPanel from "primevue/overlaypanel";
import CommodityModalAll from "./modal-all-commodity.vue";
import { commoditySchema } from "./validation-commodity";
import { parseZodError, validationCurrentIndex } from "../../utils";
import { CommodityData } from "@/domain/entities/BulkBooking";
import { ErrorBookingV3, ConfigValidationForm } from '@/domain/entities/CreateBookingV3';
import { SttAdjustmentRule } from "@/domain/entities/ShipmentBooking";

class Props {
  modelValue = prop<any>({
    required: true
  });
  id = prop<string>({
    required: true,
    default: ""
  });
  commodityIndex = prop<number>({
    required: true,
    default: 0
  });
  currentIndex = prop<number>({
    required: true,
    default: 0
  });
  error = prop<ErrorBookingV3>({
    required: true
  });
  configCommodity = prop<SttAdjustmentRule>({
    default: new SttAdjustmentRule(),
    type: Object,
  });
  isEditForm = prop<boolean>({
    default: false
  });
}

@Options({
  components: {
    OverlayPanel,
    CommodityModalAll
  },
  watch: {
    currentIndex(index: number) {
      if (index === 0) this.isFocused = false;
      if (!this.isEditForm) {
        if (validationCurrentIndex(this.commodityIndex, index)) {
          this.isFocused = true;
        }
      }
    },
    "modelValue.commodity.name"() {
      if (!this.isEditForm) {
        this.isFocused = true;
      }
    }
  }
})
export default class CommoditySection extends Vue.with(Props) {
  dataListCommodities: Commodity[] = [];
  dataRecommendedCommodities: Commodity[] = [];
  errorCommodity = false;
  isCommodityNotFound = false;
  isModalViewAll = false;
  isFocused = false;

  mounted() {
    if (this.isEditForm) {
      this.fetchCommodityList("");
    }
    this.getCommodityRecommendedList();
  }

  get disabled() {
    return this.configCommodity.rule.includes(ConfigValidationForm.NON_EDITABLE)
  }
  get isClientID() {
    const clientCountry = this.modelValue?.clientName?.clientCountry;
    if (!clientCountry) return true;
    return clientCountry.toLowerCase() === "id";
  }
  get accountIsNonForeign() {
    return AccountController.accountData.countryCode.toLowerCase() === "id" && this.isClientID;
  }
  get isLoadingCommodity() {
    return CommodityController.isLoading;
  }
  get isErrorCommodity() {
    return CommodityController.isError;
  }
  get commodities() {
    if (this.isCommodityNotFound) return []
    const commodityList =
      this.dataListCommodities instanceof CommodityList
        ? this.dataListCommodities.data
        : this.dataListCommodities;
    return commodityList.map((item) => {
      return {
        id: item.commodity_id,
        name: this.accountIsNonForeign
          ? item.commodity_name
          : item.commodity_name_en,
        code: item.commodity_code,
        status: item.commodity_status,
        isDangerousGoods: item.isDangerousGoods,
        isQuarantine: item.isQuarantine
      };
    });
  }

  fetchCommodityList = debounce(async (search: string) => {
    const commodities = await CommodityController.getCommodityList(
      new RequestListCommodity({
        search: search,
        page: 1,
        limit: 20,
        commodityStatus: "active"
      })
    );
    this.isCommodityNotFound = !commodities.data.length;
    this.dataListCommodities = commodities.data;
  }, 250);
  async fetchCommodityListInfiniteScroll(search = "") {
    CommodityController.commodities.pagination.page++;
    const commodities = await CommodityController.getCommodityList(
      new RequestListCommodity({
        search,
        page: CommodityController.commodities.pagination.page,
        limit: 20,
        commodityStatus: "active"
      })
    );
    this.isCommodityNotFound = false;
    this.dataListCommodities.push(...commodities.data);
  }
  changeCommodities() {
    this.errorCommodity = !this.modelValue.commodity;
    this.isCommodityNotFound = false;
    this.$emit("onChangeCommodities");
    this.$emit("select-commodity");
  }
  onFocusCommodityList() {
    // this.setFieldIndex(11);
    this.$emit("update:currentIndex", this.commodityIndex);
    if (!this.isEditForm) {
      this.isFocused = true;
      this.getCommodityList();
    }
  }

  async getCommodities(options: {
    limit: number;
    isRecommendedOnly?: boolean;
    page?: number;
    commodityStatus?: string;
  }) {
    const commodities = await CommodityController.getCommodityList(
      new RequestListCommodity({
        page: options.page || 1,
        limit: options.limit,
        commodityStatus: options.commodityStatus || "active",
        ...(options.isRecommendedOnly && {
          isRecommendedOnly: options.isRecommendedOnly
        })
      })
    );
    return commodities.data;
  }

  async getCommodityList() {
    this.dataListCommodities = await this.getCommodities({ limit: 20 });
    this.isCommodityNotFound = false;
  }

  async getCommodityRecommendedList() {
    this.dataRecommendedCommodities = await this.getCommodities({
      limit: 3,
      isRecommendedOnly: true
    });
    this.isCommodityNotFound = false;
  }
  ellipsisText(val: string, length: number) {
    return ellipsisString(val, length);
  }
  toggleTooltip(event: any, code: string) {
    const refs: any = this.$refs;
    refs[code].toggle(event);
  }
  onChoose(val: any) {
    const selectedValue: any = {
      id: val.commodity_id,
      name: this.accountIsNonForeign
        ? val.commodity_name
        : val.commodity_name_en,
      code: val.commodity_code,
      status: val.commodity_status,
      isDangerousGoods: val.isDangerousGoods,
      isQuarantine: val.isQuarantine
    };
    this.modelValue.commodity = selectedValue;
    this.dataListCommodities = [val];

    this.$emit("select-commodity");
  }
  isSelectedRecommendation(val: any) {
    if (this.modelValue) {
      return val === this.modelValue.commodity?.code;
    }
    return false;
  }
  clickViewAll() {
    this.isModalViewAll = !this.isModalViewAll;
  }
  get errorIndicator() {
    const value = this.modelValue.commodity
      ? this.modelValue.commodity
      : new CommodityData();
    if (this.isFocused) {
      return !!parseZodError(commoditySchema, value);
    }
    return false;
  }
  get errorMessage() {
    const value = this.modelValue.commodity
      ? this.modelValue.commodity
      : new CommodityData();
    if (this.isFocused) {
      const message = parseZodError(commoditySchema, value);
      this.error.commodity = message;
      return message
    }
    this.error.commodity = "";
    return "";
  }
}
